/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BuyerCompanies` for the Caintra Connect frontend.
 * @module pages/BuyerCompanies
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { Backdrop, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useBuyerCompaniesPage } from "../hooks/useBuyerCompaniesPage";
import styles from "../styles/BuyerCompanies.module.css";
import {
  BuyerCompaniesHeader,
  BuyerCompaniesResults,
} from "./BuyerCompaniesSections.jsx";

/**
 * Buyer-companies directory: search, sort, page, and open the create form.
 * @returns {JSX.Element|null}
 */
const BuyerCompanies = () => {
  const { t } = useTranslation();
  const page = useBuyerCompaniesPage();

  if (!page.allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <BuyerCompaniesHeader
        t={t}
        navigate={page.navigate}
        searchQuery={page.searchQuery}
        onSearch={page.handleSearch}
        onCreate={page.handleCreateBuyerCompany}
      />
      <BuyerCompaniesResults
        t={t}
        paginatedBuyerCompaniesList={page.paginatedBuyerCompaniesList}
        filteredBuyerCompaniesList={page.filteredBuyerCompaniesList}
        resultsLimit={page.resultsLimit}
        currentPage={page.currentPage}
        sortDirection={page.sortConfig.direction}
        onSort={page.handleSort}
        onResultsLimitChange={page.handleResultsLimitChange}
        onPageChange={page.handlePageChange}
      />
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={page.loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default BuyerCompanies;
